// AdvanceCurriculumCoursesController.tsx
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import AdvanceCurriculumCoursesView from './AdvanceCurriculumCoursesView';
import { Creators as planProgressActions } from '../../../../../../domain/useCases/getPlanProgressDetail';
import { Creators as planProgressEquivalencesActions } from '../../../../../../domain/useCases/getPlanProgressEquivalences';

interface Course {
  name: string;
  courseId: string;
  code: string;
  credits: number;
  type: string;
  hours: number;
  status: string;
  prerequisites: { code: string; name: string }[];
}

interface PlanProgressDetailItem {
  term: any;
  name: string;
  courseId: string;
  code: string;
  credits: string;
  type: string;
  status: string;
  hours: string;
  prerequisites: { code: string; name: string }[];
}

const convertTermNumberToText = (term: number): string => {
  const ciclo = [
    '1er',
    '2do',
    '3er',
    '4to',
    '5to',
    '6to',
    '7mo',
    '8vo',
    '9no',
    '10mo',
    '11vo',
    '12vo',
    '13vo',
    '14vo'
  ];
  if (term >= 1 && term <= 14) {
    return `${ciclo[term - 1]} ciclo`;
  }
  return `Cursos de nivelación`;
};

const convertPrerequisites = (prereq: any): { code: string; name: string } => ({
  code: prereq.code,
  name: prereq.name
});

const convertPlanProgressToViewData = (
  planProgressDetail: PlanProgressDetailItem[]
): { term: string; courses: Course[]; mandatoryCourseCount: number }[] => {
  const groupedByTerm: {
    [term: string]: { courses: Course[]; mandatoryCount: number };
  } = {};

  planProgressDetail.forEach((course: PlanProgressDetailItem) => {
    if (!groupedByTerm[course.term]) {
      groupedByTerm[course.term] = { courses: [], mandatoryCount: 0 };
    }

    const isMandatory = course.type === 'O';

    if (isMandatory) {
      groupedByTerm[course.term].mandatoryCount += +course.credits;
    }

    groupedByTerm[course.term].courses.push({
      name: course.name,
      courseId: course.courseId,
      code: course.code,
      credits: parseFloat(course.credits),
      type: course.type,
      hours: parseFloat(course.hours),
      status: course.status,
      prerequisites: course.prerequisites
        ? course.prerequisites.map(convertPrerequisites)
        : []
    });
  });

  return Object.entries(groupedByTerm)
    .sort(([termA], [termB]) => parseInt(termA, 10) - parseInt(termB, 10))
    .map(([term, { courses, mandatoryCount }]) => ({
      term: convertTermNumberToText(parseInt(term, 10)),
      courses: courses.sort((a) => (a.type === 'O' ? -1 : 1)),
      mandatoryCourseCount: mandatoryCount
    }));
};

const AdvanceCurriculumCoursesController: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { plan, progDesc, prog, career } = useSelector(
    (state: any) => state.profile.data.academic
  );
  const { firstName, lastName } = useSelector(
    (state: any) => state.profile.data.profile
  );

  const {
    data: dataPlanProgressDetail,
    isLoading: isLoadingPlanProgressDetail
  } = useSelector((state: any) => state.getPlanProgressDetail);

  const { data: equivalences, isLoading: isLoadingEquivalences } = useSelector(
    (state: any) => state.getPlanProgressEquivalences
  );
  const [convertedData, setConvertedData] = useState<
    { term: string; courses: Course[]; mandatoryCourseCount: number }[]
  >([]);

  const [selectedCoursePrerequisites, setSelectedCoursePrerequisites] =
    useState<string[]>([]);

  const [mainSelectedCoursePrerequisites, setMainSelectedCoursePrerequisites] =
    useState<string | null>('');

  const loadEquivalences = (courseId: string, courseCode: string[]) => {
    dispatch(
      planProgressEquivalencesActions.getPlanProgressEquivalences(
        courseId,
        courseCode
      )
    );
  };
  const handlerBack = () => {
    history.goBack();
  };

  useEffect(() => {
    if (plan && prog)
      dispatch(planProgressActions.getPlanProgressDetail(plan, prog, career));
  }, [plan]);

  useEffect(() => {
    if (dataPlanProgressDetail?.items) {
      const convertedDataTemp = convertPlanProgressToViewData(
        dataPlanProgressDetail.items
      );
      setConvertedData(convertedDataTemp);
    }
  }, [dataPlanProgressDetail]);

  return (
    <AdvanceCurriculumCoursesView
      data={convertedData}
      loadEquivalences={loadEquivalences}
      isLoadingEquivalences={isLoadingEquivalences}
      isLoadingPlanProgressDetail={isLoadingPlanProgressDetail}
      isProcessingPlanProgressDetail={dataPlanProgressDetail?.isProcessing}
      equivalences={equivalences}
      setSelectedCoursePrerequisites={setSelectedCoursePrerequisites}
      selectedCoursePrerequisites={selectedCoursePrerequisites}
      setMainSelectedCoursePrerequisites={setMainSelectedCoursePrerequisites}
      mainSelectedCoursePrerequisites={mainSelectedCoursePrerequisites}
      handlerBack={handlerBack}
      academicPlan={plan}
      fullName={`${lastName} .${firstName}`}
      program={progDesc}
    />
  );
};

export default AdvanceCurriculumCoursesController;
